import { cn } from '@/lib/utils';

interface CircularProgressProps {
  value: number; // 0-100
  size?: 'sm' | 'md';
  className?: string;
  showValue?: boolean;
}

/**
 * Small circular progress indicator for sidebar badges
 * Disappears when value reaches 100%
 */
export function CircularProgress({ 
  value, 
  size = 'sm', 
  className,
  showValue = true 
}: CircularProgressProps) {
  if (value >= 100) return null;
  
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
  };
  
  const strokeWidth = size === 'sm' ? 2.5 : 3;
  const radius = size === 'sm' ? 8 : 10;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (value / 100) * circumference;
  
  return (
    <div className={cn('relative inline-flex items-center justify-center', sizeClasses[size], className)}>
      <svg 
        className="transform -rotate-90 w-full h-full"
        viewBox={`0 0 ${(radius + strokeWidth) * 2} ${(radius + strokeWidth) * 2}`}
      >
        {/* Background circle */}
        <circle
          cx={radius + strokeWidth}
          cy={radius + strokeWidth}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={radius + strokeWidth}
          cy={radius + strokeWidth}
          r={radius}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-300"
        />
      </svg>
      {showValue && (
        <span className="absolute text-[8px] font-medium text-primary leading-none">
          {Math.round(value)}
        </span>
      )}
    </div>
  );
}
